/**
 * `useDateRequestCategories` + create / patch / delete — TanStack hooks for
 * the configurable date-request category catalogue (#836).
 *
 * Backed by `backend/src/api/routes/date_request_categories.py`:
 *
 *   * `GET    /api/teams/{team_id}/date-request-categories`        — list the
 *     catalogue (any active member; the grid + member surfaces need labels +
 *     colours). Seeded with the six built-ins for a fresh team.
 *   * `POST   /api/teams/{team_id}/date-request-categories`        — create a
 *     custom category (lead). Key derived from label; colour auto-assigned.
 *   * `PATCH  /api/teams/{team_id}/date-request-categories/{key}`  — relabel /
 *     recolour / re-weight / re-prioritise (lead). Built-in behaviour is
 *     locked; key immutable. `priority` (#1670) orders the lead's review
 *     queue — lower first. `blocks_night_before` (#2279, leave types) keeps
 *     the person off call on the night before the leave's first day.
 *   * `DELETE /api/teams/{team_id}/date-request-categories/{key}`  — delete a
 *     custom category (lead); built-ins can't be deleted.
 *
 * Each category maps to one solver behaviour; the catalogue never changes
 * scheduling semantics — it relabels / recolours / re-weights an existing
 * behaviour. Mutations invalidate both the catalogue and the date-requests
 * cache (a deleted category cascades to `category_key` on existing rows).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { encodePathName } from "../lib/encodePathName";
import { type ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";

/**
 * The catalogue as READ. #1670: `priority` (the lead's review-queue rank)
 * comes back `null` for anyone who is not a lead/co-lead — the server
 * scrubs it, because this read is open to every member and no member or
 * peer surface may ever carry a rank (three circles). Treat `null` as
 * "not mine to see", never as "unset".
 */
export type DateRequestCategory = components["schemas"]["DateRequestCategoryView"];
export type CategoryCreateBody = components["schemas"]["CategoryCreateBody"];
export type CategoryPatchBody = components["schemas"]["CategoryPatchBody"];

/** The closed set of solver behaviours a category may map to, with the
 *  worker-facing meaning. Only `leave` removes worked days, so the workload
 *  weight applies to it alone (the modal hides the slider otherwise). */
export const CATEGORY_BEHAVIOURS: { value: string; label: string; isLeave: boolean }[] = [
  { value: "leave", label: "Leave (away all day)", isLeave: true },
  { value: "no_oncall", label: "Call-block (not on-call)", isLeave: false },
  { value: "no_am", label: "No AM (unavailable AM)", isLeave: false },
  { value: "no_pm", label: "No PM (unavailable PM)", isLeave: false },
  { value: "prefer_am", label: "Prefer AM", isLeave: false },
  { value: "prefer_pm", label: "Prefer PM", isLeave: false },
  // #1017 (WS-4) — a fixed external commitment (dept meeting / clinic)
  // scheduled outside the team roster: the attendee is unavailable for the
  // whole day (no station, no on-call) and the roster is built around it.
  { value: "fixture", label: "Fixed commitment (busy all day)", isLeave: false },
];

// Fallback cell abbreviations for the built-ins, used when a team's stored
// category predates the `abbreviation` field (so existing teams still get
// sensible labels without a data backfill).
const BUILTIN_ABBREV: Record<string, string> = {
  leave: "L",
  no_oncall: "CB",
  no_am: "-AM",
  no_pm: "-PM",
  prefer_am: "+AM",
  prefer_pm: "+PM",
  fixture: "FX",
};

/** The short label to paint inside a grid cell: the category's own
 *  abbreviation, else the built-in fallback, else initials of the label. */
export function categoryAbbrev(cat: DateRequestCategory): string {
  if (cat.abbreviation) return cat.abbreviation;
  const builtin = BUILTIN_ABBREV[cat.key];
  if (builtin) return builtin;
  const words = cat.label.trim().split(/\s+/).filter(Boolean);
  return (words.length >= 2 ? words.map((w) => w[0]).join("") : cat.label)
    .slice(0, 3)
    .toUpperCase();
}

export const CATEGORIES_KEY = (teamId: string) => ["date-request-categories", teamId] as const;

export function useDateRequestCategories(teamId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: [...CATEGORIES_KEY(teamId ?? ""), accountId ?? ""],
    enabled: Boolean(teamId && accountId),
    retry: false,
    queryFn: () => apiFetch<DateRequestCategory[]>(`/api/teams/${teamId}/date-request-categories`),
  });
}

export function useCreateCategory(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<DateRequestCategory, ApiError | Error, CategoryCreateBody>({
    mutationFn: (body) =>
      apiFetch<DateRequestCategory>(`/api/teams/${teamId}/date-request-categories`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["date-request-categories", teamId ?? ""] }),
  });
}

export function useUpdateCategory(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    DateRequestCategory,
    ApiError | Error,
    { key: string; body: CategoryPatchBody }
  >({
    mutationFn: ({ key, body }) =>
      apiFetch<DateRequestCategory>(
        `/api/teams/${teamId}/date-request-categories/${encodePathName(key)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["date-request-categories", teamId ?? ""] });
      queryClient.invalidateQueries({ queryKey: ["team-date-requests", teamId ?? ""] });
    },
  });
}

export function useDeleteCategory(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError | Error, string>({
    mutationFn: (key) =>
      apiFetch<void>(`/api/teams/${teamId}/date-request-categories/${encodePathName(key)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      // A delete cascades to `category_key` on existing rows → refresh both.
      queryClient.invalidateQueries({ queryKey: ["date-request-categories", teamId ?? ""] });
      queryClient.invalidateQueries({ queryKey: ["team-date-requests", teamId ?? ""] });
    },
  });
}
